import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { type Term, useTermMutations } from "../../../hooks";

interface TermInfoSectionProps {
  termId: string;
  term: Term;
  refetchTerm: () => void;
}

/**
 * ターム情報カード（表示と編集モードを内包）。
 * 編集フォームは title 必須、description / notionUrl は任意。
 */
export function TermInfoSection({
  termId,
  term,
  refetchTerm,
}: TermInfoSectionProps) {
  const { updateTerm } = useTermMutations();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    title: term.title,
    description: term.description || "",
    notionUrl: term.notionUrl || "",
  });
  const [isUpdating, setIsUpdating] = useState(false);

  const handleStartEdit = () => {
    setForm({
      title: term.title,
      description: term.description || "",
      notionUrl: term.notionUrl || "",
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setForm({ title: "", description: "", notionUrl: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      alert("インタビュータイトルを入力してください");
      return;
    }
    setIsUpdating(true);
    try {
      const result = await updateTerm({
        termId,
        title: form.title.trim(),
        description: form.description.trim() || null,
        notionUrl: form.notionUrl.trim() || null,
      });
      if (result) {
        setIsEditing(false);
        refetchTerm();
      } else {
        alert("更新に失敗しました");
      }
    } catch {
      alert("更新に失敗しました");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
        <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
          ターム情報
        </h2>
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleStartEdit}
            className="text-[var(--color-primary-500)] hover:bg-[var(--color-primary-50)]"
          >
            編集
          </Button>
        )}
      </div>
      <div className="p-6">
        {isEditing ? (
          <form onSubmit={handleSubmit}>
            <div className="mb-5">
              <label
                htmlFor="edit-term-title"
                className="mb-2 block font-semibold text-[var(--color-text-primary)] text-sm"
              >
                インタビュータイトル{" "}
                <span className="text-[var(--color-text-danger)]">*</span>
              </label>
              <input
                id="edit-term-title"
                type="text"
                value={form.title}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="インタビュータイトルを入力"
                className="box-border w-full rounded-lg border border-[var(--color-border-default)] px-4 py-3 text-sm outline-none focus:border-[var(--color-primary-500)]"
              />
              <p className="mt-1 text-[var(--color-primary-500)] text-xs">
                この文言は対象者へ送るメールに出ます
              </p>
            </div>
            <div className="mb-5">
              <label
                htmlFor="edit-term-description"
                className="mb-2 block font-semibold text-[var(--color-text-primary)] text-sm"
              >
                ターム詳細
              </label>
              <textarea
                id="edit-term-description"
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="ターム詳細を入力（任意）"
                rows={4}
                className="box-border w-full resize-y rounded-lg border border-[var(--color-border-default)] px-4 py-3 text-sm outline-none focus:border-[var(--color-primary-500)]"
              />
            </div>
            <div className="mb-5">
              <label
                htmlFor="edit-term-notion-url"
                className="mb-2 block font-semibold text-[var(--color-text-primary)] text-sm"
              >
                Notion URL
              </label>
              <input
                id="edit-term-notion-url"
                type="url"
                value={form.notionUrl}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, notionUrl: e.target.value }))
                }
                placeholder="https://www.notion.so/..."
                className="box-border w-full rounded-lg border border-[var(--color-border-default)] px-4 py-3 text-sm outline-none focus:border-[var(--color-primary-500)]"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isUpdating}
              >
                キャンセル
              </Button>
              <Button type="submit" disabled={!form.title.trim() || isUpdating}>
                {isUpdating ? "保存中..." : "保存"}
              </Button>
            </div>
          </form>
        ) : (
          <>
            <div className="mb-4">
              <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                インタビュータイトル
              </p>
              <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                {term.title}
              </p>
            </div>
            <div className="mb-4">
              <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                ターム詳細
              </p>
              <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                {term.description || "-"}
              </p>
            </div>
            <div>
              <p className="mb-1 font-medium text-[13px] text-[var(--color-text-secondary)]">
                Notion URL
              </p>
              {term.notionUrl ? (
                <a
                  href={term.notionUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-[15px] text-[var(--color-primary-500)] underline"
                >
                  {term.notionUrl}
                </a>
              ) : (
                <p className="font-medium text-[15px] text-[var(--color-text-primary)]">
                  -
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
